import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useUser } from '../UserContext';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./index.css";

const fmt = (v) => Number(v || 0).toFixed(2).replace(".", ",");

const corrigirEncoding = (texto) => {
  return (texto || "")
    .replace(/Ã‰/g, "É")
    .replace(/Ã©/g, "é")
    .replace(/Ã‡/g, "Ç")
    .replace(/Ã§/g, "ç")
    .replace(/Ã/g, "Á");
};

const parsePagamentos = (str) => {
  if (!str) return [];
  return str
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => {
      const [formaRaw, valorRaw] = p.split(":");
      const forma = corrigirEncoding((formaRaw || "").trim());
      const valorNum = parseFloat(
        String(valorRaw || "0").replace(/[^\d.,]/g, "").replace(",", ".")
      ) || 0;
      return { forma, valor: valorNum };
    });
};

const hojeISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const extrairDataISO = (isoString) => (isoString ? isoString.split("T")[0] : "");

const formatarDataBR = (isoString) => {
  const data = extrairDataISO(isoString);
  if (!data) return "";
  const [ano, mes, dia] = data.split("-");
  return `${dia}/${mes}/${ano}`;
};

const TIPOS_RELATORIO = [
  { id: "vendas", label: "Relação de Vendas" },
  { id: "resumoVendas", label: "Resumo de Vendas" },
  { id: "vendasProdutos", label: "Relação de Vendas com Produtos" },
  { id: "resumoProdutos", label: "Resumo de Produtos Vendidos" },
];

const exportarCSV = (linhas, colunas, nomeArquivo) => {
  const header = colunas.map((c) => c.label).join(";");
  const rows = linhas.map((linha) =>
    colunas.map((c) => `"${String(c.valor(linha)).replace(/"/g, '""')}"`).join(";")
  );
  const csvContent = [header, ...rows].join("\n");
  const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${nomeArquivo}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export default function ModalRelatorios({ onClose }) {
  const { userInfo } = useUser();
  const [tipoRelatorio, setTipoRelatorio] = useState("vendas");
  const [dataInicio, setDataInicio] = useState(hojeISO());
  const [dataFim, setDataFim] = useState(hojeISO());
  const [vendas, setVendas] = useState([]);
  const [itensPorVenda, setItensPorVenda] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingItens, setLoadingItens] = useState(false);
  const [erro, setErro] = useState("");

  const credentials = btoa(`${userInfo.alias};${userInfo.venLogin}:${userInfo.venSenha}`);

  const precisaProdutos = tipoRelatorio === "vendasProdutos" || tipoRelatorio === "resumoProdutos";

  // ---------- Busca vendas ----------
  const fetchVendas = useCallback(async () => {
    if (!userInfo?.url || !userInfo?.alias) return;

    try {
      setLoading(true);
      setErro("");

      const res = await fetch(`${userInfo.url}/nfce`, {
        headers: {
          "Content-Type": "application/json",
          empresa: userInfo.alias,
          Authorization: `Basic ${credentials}`,
        },
      });

      if (!res.ok) {
        setErro("Erro ao buscar vendas.");
        return;
      }

      const json = await res.json();
      setVendas(Array.isArray(json) ? json : []);
    } catch (err) {
      console.error("Erro ao buscar vendas:", err);
      setErro("Erro ao buscar vendas.");
    } finally {
      setLoading(false);
    }
  }, [userInfo, credentials]);

  useEffect(() => {
    fetchVendas();
  }, [fetchVendas]);

  const vendasFiltradas = useMemo(() => {
    if (!dataInicio || !dataFim) return vendas;
    return vendas.filter((v) => {
      const dataVenda = extrairDataISO(v.cupDtaCadastro);
      return dataVenda >= dataInicio && dataVenda <= dataFim;
    });
  }, [vendas, dataInicio, dataFim]);

  // ---------- Busca itens (somente quando necessário) ----------
  useEffect(() => {
    if (!precisaProdutos || vendasFiltradas.length === 0) return;

    const cupIdsFaltantes = vendasFiltradas
      .map((v) => v.cupId)
      .filter((id) => !itensPorVenda[id]);

    if (cupIdsFaltantes.length === 0) return;

    const buscarTodos = async () => {
      try {
        setLoadingItens(true);
        const resultados = await Promise.all(
          cupIdsFaltantes.map(async (cupId) => {
            try {
              const res = await fetch(`${userInfo.url}/itemnfce/pedido/${cupId}`, {
                headers: {
                  "Content-Type": "application/json",
                  empresa: userInfo.alias,
                  Authorization: `Basic ${credentials}`,
                },
              });
              if (!res.ok) return { cupId, itens: [] };
              const json = await res.json();
              return { cupId, itens: Array.isArray(json) ? json : [] };
            } catch {
              return { cupId, itens: [] };
            }
          })
        );

        setItensPorVenda((prev) => {
          const novo = { ...prev };
          resultados.forEach(({ cupId, itens }) => {
            novo[cupId] = itens;
          });
          return novo;
        });
      } finally {
        setLoadingItens(false);
      }
    };

    buscarTodos();
  }, [precisaProdutos, vendasFiltradas, itensPorVenda, userInfo, credentials]);

  // ---------- Totalizador por status ----------
  const totalizadorStatus = useMemo(() => {
    const porStatus = {};
    vendasFiltradas.forEach((v) => {
      const status = v.cupStatus || "N/D";
      if (!porStatus[status]) porStatus[status] = { qtd: 0, total: 0 };
      porStatus[status].qtd += 1;
      porStatus[status].total += Number(v.cupTotCupom || 0);
    });
    return Object.entries(porStatus).map(([status, dados]) => ({ status, ...dados }));
  }, [vendasFiltradas]);

  // ---------- Resumo de vendas (por forma de pagamento) ----------
  const resumoVendas = useMemo(() => {
    const totalVendas = vendasFiltradas.reduce((s, v) => s + Number(v.cupTotCupom || 0), 0);
    const totalDesconto = vendasFiltradas.reduce((s, v) => s + Number(v.cupTotDesconto || 0), 0);
    const qtdVendas = vendasFiltradas.length;

    const porForma = {};
    vendasFiltradas.forEach((v) => {
      parsePagamentos(v.pagamentos).forEach(({ forma, valor }) => {
        porForma[forma] = (porForma[forma] || 0) + valor;
      });
    });

    const listaFormas = Object.entries(porForma)
      .map(([forma, valor]) => ({ forma, valor }))
      .sort((a, b) => b.valor - a.valor);

    return { totalVendas, totalDesconto, qtdVendas, listaFormas };
  }, [vendasFiltradas]);

  // ---------- Relação de vendas com produtos (linhas expandidas) ----------
  const linhasVendasProdutos = useMemo(() => {
    const linhas = [];
    vendasFiltradas.forEach((v) => {
      const itens = itensPorVenda[v.cupId] || [];
      if (itens.length === 0) {
        linhas.push({
          cupId: v.cupId,
          data: v.cupDtaCadastro,
          status: v.cupStatus,
          produto: "-",
          qtd: "-",
          valorUnit: 0,
          valorTotal: 0,
        });
      } else {
        itens.forEach((item) => {
          linhas.push({
            cupId: v.cupId,
            data: v.cupDtaCadastro,
            status: v.cupStatus,
            produto: item.proDescricao,
            qtd: item.icuQtdProduto,
            valorUnit: item.icuVlrProduto,
            valorTotal: item.icuQtdProduto * item.icuVlrProduto,
          });
        });
      }
    });
    return linhas;
  }, [vendasFiltradas, itensPorVenda]);

  // ---------- Resumo de produtos vendidos (agrupado por produto) ----------
  const resumoProdutos = useMemo(() => {
    const porProduto = {};
    vendasFiltradas.forEach((v) => {
      const itens = itensPorVenda[v.cupId] || [];
      itens.forEach((item) => {
        const nome = item.proDescricao;
        if (!porProduto[nome]) porProduto[nome] = { produto: nome, qtd: 0, total: 0 };
        porProduto[nome].qtd += Number(item.icuQtdProduto || 0);
        porProduto[nome].total += Number(item.icuQtdProduto || 0) * Number(item.icuVlrProduto || 0);
      });
    });

    return Object.values(porProduto).sort((a, b) => b.total - a.total);
  }, [vendasFiltradas, itensPorVenda]);

  const totalGeralProdutos = useMemo(
    () => resumoProdutos.reduce((s, p) => s + p.total, 0),
    [resumoProdutos]
  );

  const totalGeralQtdProdutos = useMemo(
    () => resumoProdutos.reduce((s, p) => s + p.qtd, 0),
    [resumoProdutos]
  );

  const periodoLabel = `${dataInicio.split("-").reverse().join("/")} até ${dataFim.split("-").reverse().join("/")}`;
  const tituloAtual = TIPOS_RELATORIO.find((t) => t.id === tipoRelatorio)?.label || "";

  // ================= IMPRESSÃO =================
  const imprimir = () => {
    const win = window.open("", "_blank", "width=900,height=800");
    win.document.write(`
      <html>
      <head>
        <title>${tituloAtual}</title>
        <style>
          body { font-family: Arial, sans-serif; font-size: 12px; padding: 16px; }
          h2 { margin: 0 0 4px; }
          .periodo { margin-bottom: 12px; color: #555; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
          th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
          th { background: #f2f3f5; }
          tfoot td { font-weight: bold; background: #f8f9fa; }
          h3 { margin-top: 20px; }
        </style>
      </head>
      <body onload="window.print(); window.close();">
        <h2>${userInfo.alias} - ${tituloAtual}</h2>
        <div class="periodo">Período: ${periodoLabel}</div>
        ${gerarHtmlRelatorio()}
      </body>
      </html>
    `);
    win.document.close();
  };

  const gerarHtmlRelatorio = () => {
    if (tipoRelatorio === "vendas") {
      return `
        <table>
          <thead><tr><th>Cupom</th><th>Data</th><th>Status</th><th>Mercadoria</th><th>Desconto</th><th>Total</th><th>Pagamentos</th></tr></thead>
          <tbody>
            ${vendasFiltradas.map((v) => `
              <tr>
                <td>${v.cupId}</td>
                <td>${formatarDataBR(v.cupDtaCadastro)}</td>
                <td>${v.cupStatus}</td>
                <td>R$ ${fmt(v.cupTotMercadoria)}</td>
                <td>R$ ${fmt(v.cupTotDesconto)}</td>
                <td>R$ ${fmt(v.cupTotCupom)}</td>
                <td>${corrigirEncoding(v.pagamentos || "")}</td>
              </tr>`).join("")}
          </tbody>
          <tfoot><tr><td colspan="5">TOTAL (${vendasFiltradas.length} vendas)</td><td>R$ ${fmt(resumoVendas.totalVendas)}</td><td></td></tr></tfoot>
        </table>
        <h3>Totalizador por Status</h3>
        <table>
          <thead><tr><th>Status</th><th>Qtd</th><th>Total</th></tr></thead>
          <tbody>${totalizadorStatus.map((s) => `<tr><td>${s.status}</td><td>${s.qtd}</td><td>R$ ${fmt(s.total)}</td></tr>`).join("")}</tbody>
        </table>
      `;
    }

    if (tipoRelatorio === "resumoVendas") {
      return `
        <table>
          <thead><tr><th>Descrição</th><th>Valor</th></tr></thead>
          <tbody>
            <tr><td>Quantidade de vendas</td><td>${resumoVendas.qtdVendas}</td></tr>
            <tr><td>Total de desconto</td><td>R$ ${fmt(resumoVendas.totalDesconto)}</td></tr>
            ${resumoVendas.listaFormas.map((f) => `<tr><td>${f.forma}</td><td>R$ ${fmt(f.valor)}</td></tr>`).join("")}
          </tbody>
          <tfoot><tr><td>TOTAL GERAL</td><td>R$ ${fmt(resumoVendas.totalVendas)}</td></tr></tfoot>
        </table>
        <h3>Totalizador por Status</h3>
        <table>
          <thead><tr><th>Status</th><th>Qtd</th><th>Total</th></tr></thead>
          <tbody>${totalizadorStatus.map((s) => `<tr><td>${s.status}</td><td>${s.qtd}</td><td>R$ ${fmt(s.total)}</td></tr>`).join("")}</tbody>
        </table>
      `;
    }

    if (tipoRelatorio === "vendasProdutos") {
      return `
        <table>
          <thead><tr><th>Cupom</th><th>Data</th><th>Status</th><th>Produto</th><th>Qtd</th><th>Valor Unit.</th><th>Total</th></tr></thead>
          <tbody>
            ${linhasVendasProdutos.map((l) => `
              <tr>
                <td>${l.cupId}</td>
                <td>${formatarDataBR(l.data)}</td>
                <td>${l.status}</td>
                <td>${l.produto}</td>
                <td>${l.qtd}</td>
                <td>R$ ${fmt(l.valorUnit)}</td>
                <td>R$ ${fmt(l.valorTotal)}</td>
              </tr>`).join("")}
          </tbody>
          <tfoot><tr><td colspan="6">TOTAL</td><td>R$ ${fmt(linhasVendasProdutos.reduce((s, l) => s + l.valorTotal, 0))}</td></tr></tfoot>
        </table>
      `;
    }

    if (tipoRelatorio === "resumoProdutos") {
      return `
        <table>
          <thead><tr><th>Produto</th><th>Qtd Vendida</th><th>Total</th></tr></thead>
          <tbody>
            ${resumoProdutos.map((p) => `<tr><td>${p.produto}</td><td>${p.qtd}</td><td>R$ ${fmt(p.total)}</td></tr>`).join("")}
          </tbody>
          <tfoot><tr><td>TOTAL</td><td>${totalGeralQtdProdutos}</td><td>R$ ${fmt(totalGeralProdutos)}</td></tr></tfoot>
        </table>
      `;
    }

    return "";
  };

  // ================= EXCEL (CSV) =================
  const exportarExcel = () => {
    if (tipoRelatorio === "vendas") {
      exportarCSV(
        vendasFiltradas,
        [
          { label: "Cupom", valor: (v) => v.cupId },
          { label: "Data", valor: (v) => formatarDataBR(v.cupDtaCadastro) },
          { label: "Status", valor: (v) => v.cupStatus },
          { label: "Mercadoria", valor: (v) => fmt(v.cupTotMercadoria) },
          { label: "Desconto", valor: (v) => fmt(v.cupTotDesconto) },
          { label: "Total", valor: (v) => fmt(v.cupTotCupom) },
          { label: "Pagamentos", valor: (v) => corrigirEncoding(v.pagamentos || "") },
        ],
        `relacao_vendas_${dataInicio}_a_${dataFim}`
      );
    } else if (tipoRelatorio === "resumoVendas") {
      const linhas = [
        { label: "Quantidade de vendas", valor: resumoVendas.qtdVendas },
        { label: "Total de desconto", valor: fmt(resumoVendas.totalDesconto) },
        ...resumoVendas.listaFormas.map((f) => ({ label: f.forma, valor: fmt(f.valor) })),
        { label: "TOTAL GERAL", valor: fmt(resumoVendas.totalVendas) },
      ];
      exportarCSV(linhas, [
        { label: "Descrição", valor: (l) => l.label },
        { label: "Valor", valor: (l) => l.valor },
      ], `resumo_vendas_${dataInicio}_a_${dataFim}`);
    } else if (tipoRelatorio === "vendasProdutos") {
      exportarCSV(
        linhasVendasProdutos,
        [
          { label: "Cupom", valor: (l) => l.cupId },
          { label: "Data", valor: (l) => formatarDataBR(l.data) },
          { label: "Status", valor: (l) => l.status },
          { label: "Produto", valor: (l) => l.produto },
          { label: "Qtd", valor: (l) => l.qtd },
          { label: "Valor Unit.", valor: (l) => fmt(l.valorUnit) },
          { label: "Total", valor: (l) => fmt(l.valorTotal) },
        ],
        `vendas_produtos_${dataInicio}_a_${dataFim}`
      );
    } else if (tipoRelatorio === "resumoProdutos") {
      exportarCSV(
        resumoProdutos,
        [
          { label: "Produto", valor: (p) => p.produto },
          { label: "Qtd Vendida", valor: (p) => p.qtd },
          { label: "Total", valor: (p) => fmt(p.total) },
        ],
        `resumo_produtos_${dataInicio}_a_${dataFim}`
      );
    }
  };

  // ================= PDF =================
  const exportarPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text(`${userInfo.alias} - ${tituloAtual}`, 14, 15);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Período: ${periodoLabel}`, 14, 21);

    let body = [];
    let head = [];
    let foot = [];

    if (tipoRelatorio === "vendas") {
      head = [["Cupom", "Data", "Status", "Mercadoria", "Desconto", "Total", "Pagamentos"]];
      body = vendasFiltradas.map((v) => [
        v.cupId, formatarDataBR(v.cupDtaCadastro), v.cupStatus,
        `R$ ${fmt(v.cupTotMercadoria)}`, `R$ ${fmt(v.cupTotDesconto)}`,
        `R$ ${fmt(v.cupTotCupom)}`, corrigirEncoding(v.pagamentos || ""),
      ]);
      foot = [[`TOTAL (${vendasFiltradas.length})`, "", "", "", "", `R$ ${fmt(resumoVendas.totalVendas)}`, ""]];
    } else if (tipoRelatorio === "resumoVendas") {
      head = [["Descrição", "Valor"]];
      body = [
        ["Quantidade de vendas", resumoVendas.qtdVendas],
        ["Total de desconto", `R$ ${fmt(resumoVendas.totalDesconto)}`],
        ...resumoVendas.listaFormas.map((f) => [f.forma, `R$ ${fmt(f.valor)}`]),
      ];
      foot = [["TOTAL GERAL", `R$ ${fmt(resumoVendas.totalVendas)}`]];
    } else if (tipoRelatorio === "vendasProdutos") {
      head = [["Cupom", "Data", "Status", "Produto", "Qtd", "Vlr Unit.", "Total"]];
      body = linhasVendasProdutos.map((l) => [
        l.cupId, formatarDataBR(l.data), l.status, l.produto, l.qtd,
        `R$ ${fmt(l.valorUnit)}`, `R$ ${fmt(l.valorTotal)}`,
      ]);
      foot = [["TOTAL", "", "", "", "", "", `R$ ${fmt(linhasVendasProdutos.reduce((s, l) => s + l.valorTotal, 0))}`]];
    } else if (tipoRelatorio === "resumoProdutos") {
      head = [["Produto", "Qtd Vendida", "Total"]];
      body = resumoProdutos.map((p) => [p.produto, p.qtd, `R$ ${fmt(p.total)}`]);
      foot = [["TOTAL", totalGeralQtdProdutos, `R$ ${fmt(totalGeralProdutos)}`]];
    }

    autoTable(doc, {
      startY: 27,
      head,
      body,
      foot,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [37, 99, 235] },
      footStyles: { fillColor: [240, 240, 240], textColor: 20, fontStyle: "bold" },
    });

    if (tipoRelatorio === "vendas" || tipoRelatorio === "resumoVendas") {
      const finalY = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(11);
      doc.setTextColor(0);
      doc.text("Totalizador por Status", 14, finalY);

      autoTable(doc, {
        startY: finalY + 4,
        head: [["Status", "Qtd", "Total"]],
        body: totalizadorStatus.map((s) => [s.status, s.qtd, `R$ ${fmt(s.total)}`]),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [37, 99, 235] },
      });
    }

    doc.save(`${tipoRelatorio}_${dataInicio}_a_${dataFim}.pdf`);
  };

  const carregando = loading || (precisaProdutos && loadingItens);

  return (
    <div className="modal-overlay">
      <div className="modal-box modal-relatorios">
        <div className="modal-header">
          <h3>Relatórios</h3>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        <div className="rel-tabs">
          {TIPOS_RELATORIO.map((t) => (
            <button
              key={t.id}
              className={`rel-tab ${tipoRelatorio === t.id ? "ativa" : ""}`}
              onClick={() => setTipoRelatorio(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="rel-toolbar">
          <div className="rel-filtro-data">
            <label>De</label>
            <input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
            <label>Até</label>
            <input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
            <button className="btn-refresh" onClick={fetchVendas}>⟳ Atualizar</button>
          </div>

          <div className="rel-acoes">
            <button className="btn-print" onClick={imprimir} disabled={carregando}>🖨 Imprimir</button>
            <button className="btn-export" onClick={exportarExcel} disabled={carregando}>Excel</button>
            <button className="btn-pdf" onClick={exportarPDF} disabled={carregando}>PDF</button>
          </div>
        </div>

        {erro && <div className="rel-erro">{erro}</div>}
        {carregando && <div className="rel-loading">Carregando dados...</div>}

        <div className="rel-content">
          {/* ---- RELAÇÃO DE VENDAS ---- */}
          {tipoRelatorio === "vendas" && !carregando && (
            <>
              <table className="rel-tabela">
                <thead>
                  <tr><th>Cupom</th><th>Data</th><th>Status</th><th>Mercadoria</th><th>Desconto</th><th>Total</th><th>Pagamentos</th></tr>
                </thead>
                <tbody>
                  {vendasFiltradas.length === 0 ? (
                    <tr><td colSpan={7} className="rel-empty">Nenhuma venda no período.</td></tr>
                  ) : (
                    vendasFiltradas.map((v) => (
                      <tr key={v.cupId}>
                        <td>{v.cupId}</td>
                        <td>{formatarDataBR(v.cupDtaCadastro)}</td>
                        <td><span className={`status-badge status-${v.cupStatus?.toLowerCase()}`}>{v.cupStatus}</span></td>
                        <td>R$ {fmt(v.cupTotMercadoria)}</td>
                        <td>R$ {fmt(v.cupTotDesconto)}</td>
                        <td>R$ {fmt(v.cupTotCupom)}</td>
                        <td>{corrigirEncoding(v.pagamentos || "")}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot>
                  <tr><td colSpan={5}>TOTAL ({vendasFiltradas.length} vendas)</td><td>R$ {fmt(resumoVendas.totalVendas)}</td><td></td></tr>
                </tfoot>
              </table>

              <h4 className="rel-subtitulo">Totalizador por Status</h4>
              <table className="rel-tabela rel-tabela-status">
                <thead><tr><th>Status</th><th>Qtd</th><th>Total</th></tr></thead>
                <tbody>
                  {totalizadorStatus.map((s) => (
                    <tr key={s.status}>
                      <td><span className={`status-badge status-${s.status?.toLowerCase()}`}>{s.status}</span></td>
                      <td>{s.qtd}</td>
                      <td>R$ {fmt(s.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {/* ---- RESUMO DE VENDAS ---- */}
          {tipoRelatorio === "resumoVendas" && !carregando && (
            <>
              <div className="rel-cards">
                <div className="rel-card"><span>Quantidade de vendas</span><strong>{resumoVendas.qtdVendas}</strong></div>
                <div className="rel-card"><span>Total de desconto</span><strong>R$ {fmt(resumoVendas.totalDesconto)}</strong></div>
                <div className="rel-card rel-card-total"><span>TOTAL GERAL</span><strong>R$ {fmt(resumoVendas.totalVendas)}</strong></div>
              </div>

              <h4 className="rel-subtitulo">Por Forma de Pagamento</h4>
              <table className="rel-tabela">
                <thead><tr><th>Forma</th><th>Valor</th></tr></thead>
                <tbody>
                  {resumoVendas.listaFormas.length === 0 ? (
                    <tr><td colSpan={2} className="rel-empty">Sem pagamentos no período.</td></tr>
                  ) : (
                    resumoVendas.listaFormas.map((f) => (
                      <tr key={f.forma}><td>{f.forma}</td><td>R$ {fmt(f.valor)}</td></tr>
                    ))
                  )}
                </tbody>
              </table>

              <h4 className="rel-subtitulo">Totalizador por Status</h4>
              <table className="rel-tabela rel-tabela-status">
                <thead><tr><th>Status</th><th>Qtd</th><th>Total</th></tr></thead>
                <tbody>
                  {totalizadorStatus.map((s) => (
                    <tr key={s.status}>
                      <td><span className={`status-badge status-${s.status?.toLowerCase()}`}>{s.status}</span></td>
                      <td>{s.qtd}</td>
                      <td>R$ {fmt(s.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {/* ---- VENDAS COM PRODUTOS ---- */}
          {tipoRelatorio === "vendasProdutos" && !carregando && (
            <table className="rel-tabela">
              <thead>
                <tr><th>Cupom</th><th>Data</th><th>Status</th><th>Produto</th><th>Qtd</th><th>Vlr Unit.</th><th>Total</th></tr>
              </thead>
              <tbody>
                {linhasVendasProdutos.length === 0 ? (
                  <tr><td colSpan={7} className="rel-empty">Nenhum item no período.</td></tr>
                ) : (
                  linhasVendasProdutos.map((l, idx) => (
                    <tr key={idx}>
                      <td>{l.cupId}</td>
                      <td>{formatarDataBR(l.data)}</td>
                      <td><span className={`status-badge status-${l.status?.toLowerCase()}`}>{l.status}</span></td>
                      <td>{l.produto}</td>
                      <td>{l.qtd}</td>
                      <td>R$ {fmt(l.valorUnit)}</td>
                      <td>R$ {fmt(l.valorTotal)}</td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr><td colSpan={6}>TOTAL</td><td>R$ {fmt(linhasVendasProdutos.reduce((s, l) => s + l.valorTotal, 0))}</td></tr>
              </tfoot>
            </table>
          )}

          {/* ---- RESUMO DE PRODUTOS ---- */}
          {tipoRelatorio === "resumoProdutos" && !carregando && (
            <table className="rel-tabela">
              <thead><tr><th>Produto</th><th>Qtd Vendida</th><th>Total</th></tr></thead>
              <tbody>
                {resumoProdutos.length === 0 ? (
                  <tr><td colSpan={3} className="rel-empty">Nenhum produto no período.</td></tr>
                ) : (
                  resumoProdutos.map((p) => (
                    <tr key={p.produto}>
                      <td>{p.produto}</td>
                      <td>{p.qtd}</td>
                      <td>R$ {fmt(p.total)}</td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr><td>TOTAL</td><td>{totalGeralQtdProdutos}</td><td>R$ {fmt(totalGeralProdutos)}</td></tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}